<template>
  <section class="case-library">
    <div class="library-heading">
      <div>
        <span class="library-kicker">DESIGN REFERENCE</span>
        <h2>装修案例参考库</h2>
        <p>按风格、空间和户型快速找灵感，查看整套实景并直接带入 AI 创作。</p>
      </div>
      <div class="library-summary">
        <strong>{{ filteredCases.length }}</strong>
        <span>套匹配案例</span>
        <i></i>
        <strong>{{ favoriteIds.length }}</strong>
        <span>套已收藏</span>
      </div>
    </div>

    <div class="library-toolbar">
      <label class="case-search">
        <i class="el-icon-search" aria-hidden="true"></i>
        <input
          v-model.trim="searchQuery"
          type="search"
          placeholder="搜索风格、空间、材质或案例名称"
        />
        <button
          v-if="searchQuery"
          type="button"
          title="清空搜索"
          aria-label="清空搜索"
          @click="searchQuery = ''"
        >
          <i class="el-icon-close"></i>
        </button>
      </label>
      <div class="sort-tabs" role="tablist" aria-label="案例排序">
        <button
          v-for="option in sortOptions"
          :key="option.value"
          :class="{ active: sortMode === option.value }"
          role="tab"
          :aria-selected="sortMode === option.value"
          @click="sortMode = option.value"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <div class="filter-panel">
      <div v-for="group in filterGroups" :key="group.key" class="filter-row">
        <span>{{ group.label }}</span>
        <div>
          <button
            v-for="option in group.options"
            :key="option"
            :class="{ selected: activeFilters[group.key] === option }"
            @click="setFilter(group.key, option)"
          >
            {{ option }}
          </button>
        </div>
      </div>
      <button
        v-if="hasActiveFilter"
        class="reset-filter"
        title="重置筛选"
        aria-label="重置筛选"
        @click="resetFilters"
      >
        <i class="el-icon-refresh-left"></i>
        重置
      </button>
    </div>

    <template v-if="filteredCases.length">
      <div class="case-grid">
        <article v-for="item in pagedCases" :key="item.id" class="case-card">
        <button
          class="case-cover"
          :aria-label="`查看案例：${item.title}`"
          @click="openCase(item)"
        >
          <img :src="$ossImageUrl(item.images[0], 'thumbnail')" :alt="item.title" loading="lazy" decoding="async" />
          <span v-if="item.featured" class="featured-badge">精选案例</span>
          <span class="photo-count"><i class="el-icon-picture-outline"></i>{{ item.images.length }} 张</span>
          <span class="cover-action">查看整套 <i class="el-icon-right"></i></span>
        </button>
        <button
          class="favorite-button"
          :class="{ active: isFavorite(item.id) }"
          :title="isFavorite(item.id) ? '取消收藏' : '收藏案例'"
          :aria-label="isFavorite(item.id) ? '取消收藏' : '收藏案例'"
          @click="toggleFavorite(item.id)"
        >
          <i :class="isFavorite(item.id) ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
        </button>
        <div class="case-copy">
          <div class="case-tags">
            <span>{{ item.style }}</span>
            <span>{{ item.space }}</span>
          </div>
          <h3>{{ item.title }}</h3>
          <p>{{ item.description }}</p>
          <div class="case-meta">
            <span>{{ item.layout }}</span>
            <i></i>
            <span>{{ item.area }}m²</span>
            <i></i>
            <span>{{ item.location }}</span>
          </div>
        </div>
        </article>
      </div>
      <AppPagination :page="casePage" :page-size="12" :total="filteredCases.length" :loading="false" @change="casePage = $event" />
    </template>

    <div v-else class="case-empty">
      <i class="el-icon-picture-outline-round"></i>
      <strong>没有找到匹配案例</strong>
      <span>换个关键词，或减少一项筛选条件试试。</span>
      <button @click="resetAll">查看全部案例</button>
    </div>

    <transition name="case-detail">
      <div
        v-if="activeCase"
        class="case-detail-overlay"
        role="dialog"
        aria-modal="true"
        :aria-label="activeCase.title"
        @click.self="closeCase"
      >
        <div class="case-detail-panel">
          <header class="detail-header">
            <div>
              <span>案例详情 · {{ activeCase.style }}</span>
              <h2>{{ activeCase.title }}</h2>
            </div>
            <div class="detail-header-actions">
              <button
                :class="{ active: isFavorite(activeCase.id) }"
                :title="isFavorite(activeCase.id) ? '取消收藏' : '收藏案例'"
                :aria-label="isFavorite(activeCase.id) ? '取消收藏' : '收藏案例'"
                @click="toggleFavorite(activeCase.id)"
              >
                <i :class="isFavorite(activeCase.id) ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
              </button>
              <button title="关闭详情" aria-label="关闭详情" @click="closeCase">
                <i class="el-icon-close"></i>
              </button>
            </div>
          </header>

          <div class="detail-content">
            <div class="detail-gallery">
              <button
                class="detail-main-image"
                title="查看下一张"
                :aria-label="`查看${activeCase.title}下一张图片`"
                @click="nextImage"
              >
                <img
                  :src="$ossImageUrl(activeCase.images[activeImageIndex], 'preview')"
                  :alt="`${activeCase.title} ${activeImageIndex + 1}`"
                />
                <span>{{ activeImageIndex + 1 }} / {{ activeCase.images.length }}</span>
              </button>
              <div class="thumbnail-strip">
                <button
                  v-for="(image, index) in activeCase.images"
                  :key="`${activeCase.id}-${index}`"
                  :class="{ active: activeImageIndex === index }"
                  :aria-label="`查看第 ${index + 1} 张图片`"
                  @click="activeImageIndex = index"
                >
                  <img :src="$ossImageUrl(image, 'thumbnail')" :alt="`${activeCase.title}缩略图 ${index + 1}`" loading="lazy" decoding="async" />
                </button>
              </div>
            </div>

            <aside class="detail-info">
              <div class="detail-facts">
                <div><span>风格</span><strong>{{ activeCase.style }}</strong></div>
                <div><span>空间</span><strong>{{ activeCase.space }}</strong></div>
                <div><span>户型</span><strong>{{ activeCase.layout }}</strong></div>
                <div><span>面积</span><strong>{{ activeCase.area }}m²</strong></div>
              </div>

              <section class="detail-section">
                <span>设计说明</span>
                <p>{{ activeCase.description }} {{ activeCase.story }}</p>
              </section>

              <section class="detail-section">
                <span>主要材质</span>
                <div class="material-list">
                  <em v-for="material in activeCase.materials" :key="material">{{ material }}</em>
                </div>
              </section>

              <section class="detail-section">
                <span>值得参考</span>
                <ul>
                  <li v-for="point in activeCase.highlights" :key="point">{{ point }}</li>
                </ul>
              </section>

              <div class="detail-actions">
                <button class="use-reference" @click="useAsReference">
                  <i class="el-icon-magic-stick"></i>
                  带入 AI 创作
                </button>
                <button class="secondary-action" @click="toggleFavorite(activeCase.id)">
                  <i :class="isFavorite(activeCase.id) ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                  {{ isFavorite(activeCase.id) ? '已收藏' : '收藏案例' }}
                </button>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
const imagePool = {
  localWarm: '/api/files/10bec750-2876-44a6-ac39-0462605d9a21.png',
  localModernA: '/api/files/133dc4ff-6284-4466-a16b-c4d2871f1857.png',
  localModernB: '/api/files/27c18903-5980-4eef-a4c8-0b61eb02336e.png',
  localModernC: '/api/files/345bb2c6-7fa1-4a84-a946-3674ab88c336.png',
  localModernD: '/api/files/3ff80890-8a3d-46ec-98aa-5128b3de6bb8.png',
  warmMinimal: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1400&q=86',
  modernDining: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1400&q=86',
  brightHome: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&w=1400&q=86',
  calmLiving: 'https://images.unsplash.com/photo-1618220179428-22790b461013?auto=format&fit=crop&w=1400&q=86',
  luxuryLiving: 'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1400&q=86',
  modernKitchen: 'https://images.unsplash.com/photo-1600585152915-d208bec867a1?auto=format&fit=crop&w=1400&q=86',
  softBedroom: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1400&q=86',
  quietBedroom: 'https://images.unsplash.com/photo-1615874694520-474822394e73?auto=format&fit=crop&w=1400&q=86',
  openLiving: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1400&q=86',
  naturalRoom: 'https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?auto=format&fit=crop&w=1400&q=86',
  cleanInterior: 'https://images.unsplash.com/photo-1600566753051-f0b89df2dd90?auto=format&fit=crop&w=1400&q=86'
}

const cases = [
  {
    id: 'case-01',
    title: '栖光 · 温润原木之家',
    style: '原木风',
    space: '客餐厅',
    layout: '三室两厅',
    area: 128,
    location: '杭州',
    featured: true,
    popularity: 98,
    description: '大面积浅木色与暖白墙面构成轻松通透的公共空间。',
    story: '开放式客餐厅保持清晰动线，以低矮家具和连续收纳柜减少视觉阻隔。',
    materials: ['白橡木', '哑光砖', '微水泥', '棉麻'],
    highlights: ['电视墙与餐边柜形成连续收纳', '低色温无主灯提升空间层次', '低矮家具让采光更完整'],
    images: [imagePool.localWarm, imagePool.warmMinimal, imagePool.modernDining, imagePool.brightHome]
  },
  {
    id: 'case-02',
    title: '云朵奶油 · 柔光客厅',
    style: '奶油风',
    space: '客厅',
    layout: '两室两厅',
    area: 96,
    location: '成都',
    featured: true,
    popularity: 95,
    description: '柔和奶白、圆角家具和细腻织物，让空间轻盈又松弛。',
    story: '通过统一墙顶色和弧形转角弱化边界，搭配少量深木色稳住视觉重心。',
    materials: ['奶油漆', '羊羔绒', '洞石', '深色木皮'],
    highlights: ['墙顶同色放大视觉尺度', '圆角家具降低空间锐度', '局部洞石增加自然肌理'],
    images: [imagePool.localModernA, imagePool.calmLiving, imagePool.softBedroom]
  },
  {
    id: 'case-03',
    title: '留白有序 · 城市极简',
    style: '极简风',
    space: '客餐厅',
    layout: '四室两厅',
    area: 168,
    location: '上海',
    featured: false,
    popularity: 91,
    description: '克制的色彩、隐藏式收纳和利落线条，适合高频生活。',
    story: '空间用一体化柜体隐藏设备与杂物，开放区域只保留必要家具和照明。',
    materials: ['岩板', '灰玻', '木饰面', '艺术涂料'],
    highlights: ['整墙柜体隐藏生活杂物', '线性灯强调横向尺度', '开放布局兼顾会客与用餐'],
    images: [imagePool.localModernB, imagePool.luxuryLiving, imagePool.cleanInterior]
  },
  {
    id: 'case-04',
    title: '清和雅居 · 当代新中式',
    style: '新中式',
    space: '客厅',
    layout: '四室两厅',
    area: 186,
    location: '南京',
    featured: true,
    popularity: 93,
    description: '保留东方秩序感，以现代比例表达木、石与留白。',
    story: '对称关系被适度弱化，木格栅与素色墙面共同营造安静沉稳的居住气质。',
    materials: ['胡桃木', '天然石材', '硬包', '黄铜'],
    highlights: ['传统对称关系做现代化处理', '木石组合稳重但不厚重', '暖光突出材质细节'],
    images: [imagePool.localModernC, imagePool.openLiving, imagePool.warmMinimal]
  },
  {
    id: 'case-05',
    title: '一隅好眠 · 静谧主卧',
    style: '现代简约',
    space: '主卧',
    layout: '三室两厅',
    area: 118,
    location: '苏州',
    featured: false,
    popularity: 87,
    description: '温和中性色与包裹式床头背景，营造安静的睡眠环境。',
    story: '衣柜、床头和梳妆区以同一条水平线组织，让有限面积显得完整有序。',
    materials: ['木饰面', '皮革', '棉麻', '艺术漆'],
    highlights: ['床头背景整合灯光与插座', '通顶衣柜减少卫生死角', '窗边留出轻量阅读位'],
    images: [imagePool.softBedroom, imagePool.quietBedroom, imagePool.calmLiving]
  },
  {
    id: 'case-06',
    title: '烟灰鎏金 · 轻奢会客厅',
    style: '轻奢风',
    space: '客厅',
    layout: '大平层',
    area: 220,
    location: '深圳',
    featured: false,
    popularity: 90,
    description: '低饱和灰色打底，用石材、金属和玻璃控制精致感。',
    story: '高质感材料集中在背景墙与灯具，软装保持简洁，避免空间过度堆砌。',
    materials: ['天然大理石', '拉丝金属', '灰玻', '丝绒'],
    highlights: ['重点区域使用高质感材料', '深浅灰建立稳重层次', '玻璃与灯带减轻厚重感'],
    images: [imagePool.luxuryLiving, imagePool.openLiving, imagePool.cleanInterior]
  },
  {
    id: 'case-07',
    title: '晨光料理 · 开放式厨房',
    style: '现代简约',
    space: '厨房',
    layout: '三室两厅',
    area: 135,
    location: '武汉',
    featured: false,
    popularity: 84,
    description: '岛台连接烹饪、备餐与早餐，让厨房成为家庭互动中心。',
    story: '高柜集中设备和储物，操作台保持完整连续，客餐厨之间可看见又互不干扰。',
    materials: ['石英石', '烤漆板', '橡木', '不锈钢'],
    highlights: ['岛台同时承担备餐和简餐', '高柜集中收纳大型电器', '连续台面提升操作效率'],
    images: [imagePool.modernKitchen, imagePool.modernDining, imagePool.brightHome]
  },
  {
    id: 'case-08',
    title: '森呼吸 · 自然系儿童房',
    style: '原木风',
    space: '儿童房',
    layout: '三室两厅',
    area: 112,
    location: '合肥',
    featured: false,
    popularity: 82,
    description: '自然木色、柔和绿色和可成长家具，兼顾玩耍与学习。',
    story: '采用可调整书桌和开放格，给不同年龄阶段留下变化余地。',
    materials: ['桦木板', '环保漆', '软木', '棉麻'],
    highlights: ['家具高度可随年龄调整', '开放格方便孩子自主收纳', '避免过度低龄化的固定主题'],
    images: [imagePool.naturalRoom, imagePool.brightHome, imagePool.quietBedroom]
  },
  {
    id: 'case-09',
    title: '小而舒展 · 72㎡通透两居',
    style: '奶油风',
    space: '全屋',
    layout: '两室一厅',
    area: 72,
    location: '北京',
    featured: true,
    popularity: 96,
    description: '用统一材质和开放布局释放小户型的采光与收纳能力。',
    story: '拆分笨重隔断，以半开放柜体划分功能，保证储物同时保留视线延伸。',
    materials: ['浅色木地板', '奶油漆', '长虹玻璃', '洞石'],
    highlights: ['半开放隔断保持通透', '统一地面材质拉伸空间', '转角位置全部转化为收纳'],
    images: [imagePool.calmLiving, imagePool.localModernD, imagePool.modernKitchen, imagePool.softBedroom]
  },
  {
    id: 'case-10',
    title: '黑白之间 · 理性极简之家',
    style: '极简风',
    space: '全屋',
    layout: '三室两厅',
    area: 142,
    location: '广州',
    featured: false,
    popularity: 86,
    description: '黑白灰控制得干净克制，以光影和材质差异补足层次。',
    story: '无把手柜体和隐形门统一立面，少量木色中和冷感。',
    materials: ['微水泥', '深色木皮', '岩板', '黑钛'],
    highlights: ['柜门与墙面保持同一分缝', '黑色只用于轮廓和重点', '用洗墙光强化细腻纹理'],
    images: [imagePool.cleanInterior, imagePool.luxuryLiving, imagePool.openLiving]
  },
  {
    id: 'case-11',
    title: '松弛日常 · 阳光次卧',
    style: '现代简约',
    space: '次卧',
    layout: '两室两厅',
    area: 89,
    location: '厦门',
    featured: false,
    popularity: 79,
    description: '低预算也能通过干净配色、良好采光和软装比例获得舒适感。',
    story: '减少固定造型，把预算放在床垫、窗帘和照明这些高频体验上。',
    materials: ['乳胶漆', '复合木地板', '棉麻', '白蜡木'],
    highlights: ['用软装替代复杂硬装造型', '窗帘离地尺寸更利落', '床侧保留完整通行动线'],
    images: [imagePool.quietBedroom, imagePool.softBedroom, imagePool.naturalRoom]
  },
  {
    id: 'case-12',
    title: '木石相生 · 安静大宅',
    style: '新中式',
    space: '全屋',
    layout: '别墅',
    area: 320,
    location: '宁波',
    featured: false,
    popularity: 89,
    description: '天然木石与现代家具共同建立沉稳、克制的东方居所。',
    story: '公共空间强调轴线与借景，私密空间则降低材质对比，保持安静舒适。',
    materials: ['深色木饰面', '天然石材', '藤编', '亚麻'],
    highlights: ['公共空间通过借景形成层次', '大面积留白平衡深木色', '家具比例低矮舒展'],
    images: [imagePool.localModernD, imagePool.openLiving, imagePool.warmMinimal, imagePool.naturalRoom]
  }
]

const styleOptions = [
  '现代简约',
  '奶油风',
  '原木风',
  '轻奢风',
  '新中式',
  '极简风',
  '日式风',
  '北欧风',
  '法式风',
  '美式风',
  '中古风',
  '侘寂风',
  '工业风',
  '意式风',
  '复古风',
  '南洋风',
  '混搭风'
]

const localCaseImages = [
  '/cases/modern-minimal.png',
  '/cases/cream-style.png',
  '/cases/natural-wood.png',
  '/cases/light-luxury.png',
  '/cases/new-chinese.png',
  '/cases/minimalist.png',
  '/cases/source-natural-room.jpg',
  '/cases/source-bright-home.jpg',
  '/cases/source-soft-bedroom.jpg',
  '/cases/source-quiet-bedroom.jpg',
  '/cases/source-open-living.jpg',
  '/cases/source-clean-interior.jpg',
  '/cases/source-luxury-living.jpg',
  '/cases/source-modern-kitchen.jpg',
  '/cases/source-warm-minimal.jpg',
  '/cases/source-calm-living.jpg',
  '/cases/source-modern-dining.jpg'
]

const caseSeeds = [
  {
    title: '微光留白 · 城市松弛之家',
    style: '现代简约',
    space: '客餐厅',
    layout: '三室两厅',
    area: 128,
    location: '杭州',
    description: '低饱和中性色、清晰动线与适量木色，构成耐看又易维护的日常空间。',
    story: '客餐厅保持开放关系，以整墙收纳、无主灯和轻体量家具减少视觉负担。',
    materials: ['木饰面', '哑光砖', '艺术涂料', '棉麻'],
    highlights: ['收纳柜与电视墙形成完整立面', '无主灯照明兼顾层次与实用', '家具尺度为通行留出余量'],
    gallery: [5, 7]
  },
  {
    title: '云朵奶油 · 柔光公寓',
    style: '奶油风',
    space: '客厅',
    layout: '两室两厅',
    area: 96,
    location: '成都',
    description: '奶白墙面、柔软织物与圆角家具，让空间轻盈温暖而不过分甜腻。',
    story: '墙顶使用相近色温，加入少量深木与金属细节，稳定空间的视觉重心。',
    materials: ['奶油漆', '羊羔绒', '洞石', '深色木皮'],
    highlights: ['墙顶同色放大空间感', '圆角家具降低视觉锐度', '用深木色平衡浅色软装'],
    gallery: [8, 2]
  },
  {
    title: '栖木日常 · 自然原木宅',
    style: '原木风',
    space: '全屋',
    layout: '三室两厅',
    area: 118,
    location: '苏州',
    description: '以浅木色为主线，结合白墙、亚麻和自然采光，营造清爽温润的居住感。',
    story: '木饰面从玄关延伸到客餐厅，统一柜体比例，让收纳融入空间背景。',
    materials: ['白橡木', '木地板', '微水泥', '亚麻'],
    highlights: ['统一木色避免空间零碎', '开放格与封闭柜控制展示比例', '自然光突出木材纹理'],
    gallery: [6, 7]
  },
  {
    title: '鎏金灰境 · 都市轻奢',
    style: '轻奢风',
    space: '客厅',
    layout: '大平层',
    area: 220,
    location: '深圳',
    description: '用石材、金属和玻璃控制精致感，整体色彩克制，避免繁复堆砌。',
    story: '高质感材料集中在背景墙与灯具，软装保持简洁，让空间显得稳重而通透。',
    materials: ['天然石材', '拉丝金属', '灰玻', '丝绒'],
    highlights: ['重点区域集中使用高质感材料', '深浅灰建立稳重层次', '玻璃与灯带减轻厚重感'],
    gallery: [13, 8]
  },
  {
    title: '清和雅居 · 当代东方',
    style: '新中式',
    space: '客餐厅',
    layout: '四室两厅',
    area: 186,
    location: '南京',
    description: '保留东方秩序感，以现代比例表达木、石、留白与含蓄的装饰细节。',
    story: '适度弱化传统对称，借木格栅和素色墙面营造安静沉稳的居住气质。',
    materials: ['胡桃木', '天然石材', '硬包', '黄铜'],
    highlights: ['传统秩序做现代化处理', '木石组合沉稳但不厚重', '暖光突出材质细节'],
    gallery: [11, 2]
  },
  {
    title: '黑白之间 · 纯粹极简宅',
    style: '极简风',
    space: '全屋',
    layout: '三室两厅',
    area: 142,
    location: '广州',
    description: '克制色彩、隐藏式收纳和利落线条，让空间保持纯粹、安静与高效。',
    story: '无把手柜体和隐形门统一立面，只保留必要家具，并用材质差异补充层次。',
    materials: ['微水泥', '深色木皮', '岩板', '黑钢'],
    highlights: ['柜门与墙面保持同一分缝', '黑色仅用于轮廓和重点', '洗墙光强化细腻纹理'],
    gallery: [0, 13]
  },
  {
    title: '木漏日光 · 日式和居',
    style: '日式风',
    space: '客厅',
    layout: '两室两厅',
    area: 88,
    location: '厦门',
    description: '低矮家具、自然木色与通透采光，共同形成安静而有呼吸感的生活场景。',
    story: '减少固定造型，利用障子感隔断、榻榻米坐区和可移动家具保留空间弹性。',
    materials: ['白蜡木', '藤编', '和纸灯', '亚麻'],
    highlights: ['低矮家具让视线更开阔', '轻隔断兼顾采光与分区', '留出可坐可卧的弹性区域'],
    gallery: [2, 11]
  },
  {
    title: '晴雪北境 · 明亮北欧家',
    style: '北欧风',
    space: '客餐厅',
    layout: '三室两厅',
    area: 108,
    location: '青岛',
    description: '明亮白墙、浅木地板和实用家具组合出轻松、自然、富有生活感的空间。',
    story: '在清爽基础色中加入少量灰蓝与植物，通过织物提升温度和居住舒适度。',
    materials: ['浅色木地板', '乳胶漆', '棉麻', '羊毛'],
    highlights: ['高明度基底改善采光感受', '跳色集中在可替换软装', '简洁家具保留完整活动区'],
    gallery: [2, 0]
  },
  {
    title: '巴黎柔调 · 法式轻雅',
    style: '法式风',
    space: '主卧',
    layout: '三室两厅',
    area: 132,
    location: '上海',
    description: '石膏线、柔和弧线与浅色布艺共同呈现轻盈优雅的法式居住氛围。',
    story: '降低传统雕花密度，以简化线框、复古灯具和精细软装保留浪漫气质。',
    materials: ['石膏线', '人字拼木地板', '丝绒', '黄铜'],
    highlights: ['简化线框避免墙面过度繁复', '弧形家具呼应法式柔美感', '复古灯具作为视觉焦点'],
    gallery: [1, 9]
  },
  {
    title: '炉火书香 · 美式客厅',
    style: '美式风',
    space: '客厅',
    layout: '别墅',
    area: 260,
    location: '北京',
    description: '壁炉、整墙书柜和舒展家具尺度，塑造温暖、稳重又适合家庭交流的空间。',
    story: '以经典轴线组织会客区，木质家具和织物软装共同提升包裹感与耐久度。',
    materials: ['实木护墙板', '壁炉石材', '皮革', '棉麻'],
    highlights: ['壁炉成为空间视觉中心', '对称陈设建立稳重秩序', '大尺度座椅适合家庭聚会'],
    gallery: [14, 8]
  },
  {
    title: '胡桃时光 · 中古客厅',
    style: '中古风',
    space: '客厅',
    layout: '三室两厅',
    area: 125,
    location: '重庆',
    description: '胡桃木、焦糖皮革与经典家具轮廓，带来有年代感但不过时的居住气质。',
    story: '保留大面积留白，让中古家具、艺术画和小型灯具成为空间中的重点收藏。',
    materials: ['胡桃木', '焦糖皮革', '黄铜', '羊毛地毯'],
    highlights: ['经典单品之间保留呼吸距离', '暖棕色与绿植形成自然对比', '用局部照明强化收藏氛围'],
    gallery: [14, 16]
  },
  {
    title: '寂土微光 · 侘寂居所',
    style: '侘寂风',
    space: '全屋',
    layout: '两室一厅',
    area: 82,
    location: '大理',
    description: '粗粝肌理、土色光影和不刻意的留白，呈现质朴、安静的时间感。',
    story: '弱化装饰和鲜艳色彩，以手作感器物、自然材料和柔和侧光塑造空间情绪。',
    materials: ['微水泥', '夯土涂料', '老木', '手工陶'],
    highlights: ['不规则肌理保留手作痕迹', '低饱和土色统一空间情绪', '侧光让墙面产生细微变化'],
    gallery: [6, 5]
  },
  {
    title: '钢木界面 · 工业阁楼',
    style: '工业风',
    space: '客餐厅',
    layout: '大平层',
    area: 165,
    location: '武汉',
    description: '黑色钢构、裸露质感与温暖木材形成对比，空间硬朗但不显冰冷。',
    story: '保留建筑结构和开阔层高，将设备管线整理成秩序，用家具划分不同功能区。',
    materials: ['黑钢', '清水混凝土', '旧木', '皮革'],
    highlights: ['结构构件直接成为空间表达', '木色与织物中和冷硬材质', '开放布局适合灵活调整'],
    gallery: [16, 5]
  },
  {
    title: '米兰序曲 · 意式大宅',
    style: '意式风',
    space: '客餐厅',
    layout: '大平层',
    area: 238,
    location: '杭州',
    description: '高级灰、天然石材和精确比例，呈现内敛、考究的当代意式质感。',
    story: '通过横向构图和低重心家具拉伸空间，灯光集中表现石材、木皮与皮革细节。',
    materials: ['天然大理石', '烟熏木皮', '皮革', '金属'],
    highlights: ['低重心家具强化横向延伸', '大块面材质减少视觉切割', '重点照明突出材质品质'],
    gallery: [3, 5]
  },
  {
    title: '旧日新章 · 都市复古家',
    style: '复古风',
    space: '客厅',
    layout: '两室两厅',
    area: 103,
    location: '天津',
    description: '焦糖色、旧木与复古图案共同营造亲切氛围，同时保留现代生活的便利。',
    story: '古董感家具与现代基础装修搭配，通过画作、灯具和织物控制复古浓度。',
    materials: ['旧木', '皮革', '花砖', '彩色玻璃'],
    highlights: ['复古元素集中在可替换软装', '暖色家具提升空间亲切感', '现代收纳隐藏日常杂物'],
    gallery: [10, 9]
  },
  {
    title: '蕉影长廊 · 南洋慢居',
    style: '南洋风',
    space: '客餐厅',
    layout: '别墅',
    area: 198,
    location: '海口',
    description: '藤编、深木、拱形元素与茂盛绿植，营造通风、松弛且富有度假感的家。',
    story: '利用长窗、百叶和穿堂动线改善通风，以热带植物和自然材质连接室内外。',
    materials: ['藤编', '柚木', '花砖', '百叶'],
    highlights: ['自然通风成为布局的重要条件', '深木与浅墙形成清晰层次', '绿植强化室内外连续感'],
    gallery: [16, 14]
  },
  {
    title: '自由拼图 · 艺术混搭宅',
    style: '混搭风',
    space: '全屋',
    layout: '四室两厅',
    area: 156,
    location: '长沙',
    description: '现代底色融合中古家具、工业细节和旅行收藏，形成鲜明但有秩序的个性空间。',
    story: '用统一的木色和墙面色控制整体，再通过家具、艺术品和织物表达不同风格。',
    materials: ['木饰面', '金属', '彩色织物', '艺术涂料'],
    highlights: ['统一背景色包容不同风格单品', '主次明确避免元素彼此争抢', '收藏品按色彩与尺度成组陈列'],
    gallery: [15, 12]
  }
]

const curatedCases = caseSeeds.map((item, index) => ({
  id: `case-${String(index + 1).padStart(2, '0')}`,
  featured: [0, 1, 4, 10, 15].includes(index),
  popularity: 98 - index,
  ...item,
  images: [
    localCaseImages[index],
    localCaseImages[item.gallery[0]],
    localCaseImages[item.gallery[1]]
  ]
}))

export default {
  name: 'CaseLibrary',
  data() {
    return {
      searchQuery: '',
      sortMode: 'recommended',
      activeFilters: {
        style: '全部',
        space: '全部',
        layout: '全部'
      },
      favoriteIds: ['case-01', 'case-09'],
      activeCase: null,
      activeImageIndex: 0,
      sortOptions: [
        { label: '推荐', value: 'recommended' },
        { label: '最受欢迎', value: 'popular' },
        { label: '面积从小到大', value: 'area' }
      ],
      filterGroups: [
        { key: 'style', label: '风格', options: ['全部', ...styleOptions] },
        { key: 'space', label: '空间', options: ['全部', '全屋', '客厅', '客餐厅', '主卧', '次卧', '儿童房', '厨房'] },
        { key: 'layout', label: '户型', options: ['全部', '两室一厅', '两室两厅', '三室两厅', '四室两厅', '大平层', '别墅'] }
      ],
      cases: curatedCases
      , casePage: 1
    }
  },
  computed: {
    hasActiveFilter() {
      return Boolean(this.searchQuery) || Object.values(this.activeFilters).some(value => value !== '全部')
    },
    filteredCases() {
      const keyword = this.searchQuery.toLowerCase()
      const matched = this.cases.filter(item => {
        const matchesKeyword = !keyword || [
          item.title,
          item.style,
          item.space,
          item.layout,
          item.location,
          item.description,
          ...item.materials
        ].join(' ').toLowerCase().includes(keyword)
        const matchesStyle = this.activeFilters.style === '全部' || item.style === this.activeFilters.style
        const matchesSpace = this.activeFilters.space === '全部' || item.space === this.activeFilters.space
        const matchesLayout = this.activeFilters.layout === '全部' || item.layout === this.activeFilters.layout
        return matchesKeyword && matchesStyle && matchesSpace && matchesLayout
      })

      return [...matched].sort((left, right) => {
        if (this.sortMode === 'popular') return right.popularity - left.popularity
        if (this.sortMode === 'area') return left.area - right.area
        if (left.featured !== right.featured) return left.featured ? -1 : 1
        return right.popularity - left.popularity
      })
    },
    pagedCases() { return this.filteredCases.slice((this.casePage - 1) * 12, this.casePage * 12) }
  },
  watch: {
    searchQuery() { this.casePage = 1 },
    sortMode() { this.casePage = 1 },
    activeFilters: { deep: true, handler() { this.casePage = 1 } }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
    document.body.classList.remove('case-detail-open')
  },
  methods: {
    setFilter(key, value) {
      this.$set(this.activeFilters, key, value)
    },
    resetFilters() {
      this.activeFilters = { style: '全部', space: '全部', layout: '全部' }
    },
    resetAll() {
      this.searchQuery = ''
      this.resetFilters()
      this.sortMode = 'recommended'
    },
    isFavorite(id) {
      return this.favoriteIds.includes(id)
    },
    toggleFavorite(id) {
      if (this.isFavorite(id)) {
        this.favoriteIds = this.favoriteIds.filter(item => item !== id)
      } else {
        this.favoriteIds = [...this.favoriteIds, id]
      }
    },
    openCase(item) {
      this.activeCase = item
      this.activeImageIndex = 0
      document.body.classList.add('case-detail-open')
    },
    closeCase() {
      this.activeCase = null
      this.activeImageIndex = 0
      document.body.classList.remove('case-detail-open')
    },
    nextImage() {
      if (!this.activeCase) return
      this.activeImageIndex = (this.activeImageIndex + 1) % this.activeCase.images.length
    },
    handleKeydown(event) {
      if (!this.activeCase) return
      if (event.key === 'Escape') this.closeCase()
      if (event.key === 'ArrowRight') this.nextImage()
      if (event.key === 'ArrowLeft') {
        this.activeImageIndex = (this.activeImageIndex - 1 + this.activeCase.images.length) % this.activeCase.images.length
      }
    },
    useAsReference() {
      const payload = {
        id: this.activeCase.id,
        title: this.activeCase.title,
        style: this.activeCase.style,
        space: this.activeCase.space === '全屋' ? '客餐厅' : this.activeCase.space,
        image: this.activeCase.images[this.activeImageIndex],
        prompt: `${this.activeCase.description}${this.activeCase.story} 主要材质：${this.activeCase.materials.join('、')}。`
      }
      this.closeCase()
      this.$emit('use-reference', payload)
    }
  }
}
</script>

<style scoped>
.case-library { width: calc(100% - 68px); max-width: none; margin: 28px auto 54px; }
.library-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 20px; }
.library-kicker { display: block; margin-bottom: 7px; color: #2563eb; font-size: 10px; font-weight: 700; letter-spacing: 1.6px; }
.library-heading h2 { margin: 0; color: #1c2922; font-size: 25px; letter-spacing: 0; }
.library-heading p { margin: 7px 0 0; color: #7b867f; font-size: 12px; }
.library-summary { display: flex; align-items: baseline; gap: 7px; color: #8a948d; font-size: 10px; white-space: nowrap; }
.library-summary strong { color: #26342c; font-size: 20px; font-variant-numeric: tabular-nums; }
.library-summary i { width: 1px; height: 20px; margin: 0 8px; background: #dfe5e0; }
.library-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 14px 16px; border: 1px solid #e0e5e1; border-bottom: 0; background: #fff; }
.case-search { width: min(480px, 50%); height: 40px; display: flex; align-items: center; gap: 9px; border: 1px solid #dce2dd; border-radius: 5px; padding: 0 11px; background: #f9faf9; color: #89948d; }
.case-search:focus-within { border-color: #8aabef; box-shadow: 0 0 0 3px rgba(37,99,235,.08); background: #fff; }
.case-search > i { flex: 0 0 auto; font-size: 15px; }
.case-search input { width: 100%; min-width: 0; border: 0; outline: 0; background: transparent; color: #26342c; font-size: 12px; }
.case-search input::placeholder { color: #a0aaa4; }
.case-search button { width: 26px; height: 26px; flex: 0 0 26px; border: 0; border-radius: 3px; background: transparent; color: #9aa39d; }
.case-search button:hover { background: #edf1ee; color: #526059; }
.sort-tabs { display: flex; align-items: center; gap: 3px; padding: 3px; border-radius: 5px; background: #eef1ef; }
.sort-tabs button { height: 32px; border: 0; border-radius: 3px; padding: 0 12px; background: transparent; color: #7b867f; font-size: 10px; white-space: nowrap; }
.sort-tabs button.active { background: #fff; color: #2355b5; box-shadow: 0 2px 6px rgba(36,51,43,.08); }
.filter-panel { position: relative; display: grid; gap: 10px; padding: 14px 86px 14px 16px; border: 1px solid #e0e5e1; background: #fff; }
.filter-row { min-width: 0; display: grid; grid-template-columns: 46px minmax(0, 1fr); align-items: center; gap: 12px; }
.filter-row > span { color: #8b958e; font-size: 10px; }
.filter-row > div { display: flex; align-items: center; flex-wrap: wrap; gap: 5px; }
.filter-row button { min-height: 28px; border: 0; border-radius: 4px; padding: 0 10px; background: transparent; color: #56635b; font-size: 10px; }
.filter-row button:hover { background: #f2f5f3; }
.filter-row button.selected { background: #eaf2ff; color: #1f57bd; font-weight: 600; }
.reset-filter { position: absolute; top: 14px; right: 16px; height: 30px; display: flex; align-items: center; gap: 5px; border: 1px solid #dfe4e0; border-radius: 4px; padding: 0 9px; background: #fff; color: #7b867f; font-size: 10px; }
.reset-filter:hover { border-color: #9db6e8; color: #2563eb; }
.case-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; margin-top: 20px; }
.case-card { min-width: 0; position: relative; overflow: hidden; border: 1px solid #e0e5e1; border-radius: 7px; background: #fff; box-shadow: 0 6px 22px rgba(35,50,42,.04); transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease; }
.case-card:hover { transform: translateY(-2px); border-color: #cbd5ce; box-shadow: 0 12px 30px rgba(35,50,42,.09); }
.case-cover { width: 100%; aspect-ratio: 16 / 10; position: relative; display: block; overflow: hidden; border: 0; padding: 0; background: #e7ebe8; text-align: left; }
.case-cover::after { content: ''; position: absolute; inset: auto 0 0; height: 42%; background: linear-gradient(transparent, rgba(12,20,16,.5)); opacity: .7; transition: opacity .2s ease; }
.case-cover img { width: 100%; height: 100%; display: block; object-fit: cover; transition: transform .35s ease; }
.case-card:hover .case-cover img { transform: scale(1.025); }
.featured-badge, .photo-count { position: absolute; z-index: 1; top: 10px; border-radius: 3px; padding: 5px 7px; font-size: 9px; line-height: 1; }
.featured-badge { left: 10px; background: rgba(255,255,255,.92); color: #1e58c0; font-weight: 700; }
.photo-count { right: 10px; display: flex; align-items: center; gap: 4px; background: rgba(24,34,28,.72); color: #fff; }
.cover-action { position: absolute; z-index: 1; right: 12px; bottom: 11px; display: flex; align-items: center; gap: 5px; color: #fff; font-size: 10px; opacity: 0; transform: translateY(4px); transition: .2s ease; }
.case-card:hover .cover-action { opacity: 1; transform: none; }
.favorite-button { width: 34px; height: 34px; position: absolute; z-index: 2; top: 44px; right: 10px; display: grid; place-items: center; border: 1px solid rgba(255,255,255,.5); border-radius: 4px; background: rgba(22,31,26,.72); color: #fff; font-size: 16px; opacity: 0; transition: .18s; }
.case-card:hover .favorite-button, .favorite-button.active, .favorite-button:focus-visible { opacity: 1; }
.favorite-button.active { background: #fff; color: #d39722; }
.case-copy { padding: 15px 16px 16px; }
.case-tags { display: flex; align-items: center; gap: 6px; margin-bottom: 9px; }
.case-tags span { border-radius: 3px; padding: 4px 7px; background: #f0f4f1; color: #637068; font-size: 9px; line-height: 1; }
.case-tags span:first-child { background: #eaf2ff; color: #2d61c2; }
.case-copy h3 { margin: 0; color: #26342c; font-size: 15px; letter-spacing: 0; }
.case-copy p { height: 34px; margin: 7px 0 12px; overflow: hidden; color: #7c8780; font-size: 10px; line-height: 1.65; }
.case-meta { display: flex; align-items: center; gap: 7px; color: #939d96; font-size: 9px; }
.case-meta i { width: 2px; height: 2px; border-radius: 50%; background: #aeb6b1; }
.case-empty { min-height: 360px; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 8px; margin-top: 20px; border: 1px dashed #d4dbd6; background: #fff; color: #8d9790; }
.case-empty > i { font-size: 34px; color: #9bb5ea; }
.case-empty strong { color: #48564e; font-size: 14px; }
.case-empty span { font-size: 10px; }
.case-empty button { margin-top: 7px; border: 1px solid #8eaaE6; border-radius: 4px; padding: 8px 13px; background: #fff; color: #2563eb; font-size: 10px; }
.case-detail-overlay { position: fixed; z-index: 100; inset: 0; display: flex; align-items: stretch; justify-content: flex-end; background: rgba(13,20,16,.55); backdrop-filter: blur(3px); }
.case-detail-panel { width: min(1120px, calc(100% - 74px)); height: 100%; display: grid; grid-template-rows: auto minmax(0, 1fr); background: #f4f6f4; box-shadow: -24px 0 70px rgba(10,18,13,.2); }
.detail-header { min-height: 82px; display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 14px 22px; border-bottom: 1px solid #e1e6e2; background: #fff; }
.detail-header span { color: #819087; font-size: 9px; }
.detail-header h2 { margin: 4px 0 0; color: #233028; font-size: 19px; letter-spacing: 0; }
.detail-header-actions { display: flex; gap: 8px; }
.detail-header-actions button { width: 38px; height: 38px; display: grid; place-items: center; border: 1px solid #dfe5e0; border-radius: 4px; background: #fff; color: #637068; font-size: 17px; }
.detail-header-actions button:hover { border-color: #98afe0; color: #2563eb; }
.detail-header-actions button.active { color: #d39722; }
.detail-content { min-height: 0; display: grid; grid-template-columns: minmax(0, 1.65fr) minmax(310px, .7fr); gap: 0; }
.detail-gallery { min-width: 0; min-height: 0; display: grid; grid-template-rows: minmax(0, 1fr) 92px; gap: 11px; padding: 20px; background: #e9ece9; }
.detail-main-image { min-width: 0; min-height: 0; position: relative; overflow: hidden; border: 0; border-radius: 4px; padding: 0; background: #dce1dd; }
.detail-main-image img { width: 100%; height: 100%; display: block; object-fit: cover; }
.detail-main-image > span { position: absolute; right: 12px; bottom: 12px; border-radius: 3px; padding: 5px 8px; background: rgba(16,24,19,.74); color: #fff; font-size: 9px; }
.thumbnail-strip { min-width: 0; display: grid; grid-auto-flow: column; grid-auto-columns: minmax(110px, 1fr); gap: 9px; overflow-x: auto; }
.thumbnail-strip button { min-width: 0; overflow: hidden; border: 2px solid transparent; border-radius: 4px; padding: 0; background: #dfe4e0; opacity: .58; }
.thumbnail-strip button.active { border-color: #2563eb; opacity: 1; }
.thumbnail-strip img { width: 100%; height: 100%; display: block; object-fit: cover; }
.detail-info { min-height: 0; overflow-y: auto; padding: 22px; border-left: 1px solid #e1e6e2; background: #fff; }
.detail-facts { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #e4e8e5; }
.detail-facts div { min-height: 64px; display: flex; flex-direction: column; justify-content: center; gap: 4px; padding: 11px 13px; }
.detail-facts div:nth-child(odd) { border-right: 1px solid #e4e8e5; }
.detail-facts div:nth-child(-n + 2) { border-bottom: 1px solid #e4e8e5; }
.detail-facts span, .detail-section > span { color: #8b958f; font-size: 9px; }
.detail-facts strong { color: #2a372f; font-size: 12px; }
.detail-section { padding: 18px 0; border-bottom: 1px solid #ecefec; }
.detail-section p { margin: 8px 0 0; color: #657169; font-size: 10px; line-height: 1.85; }
.material-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 9px; }
.material-list em { border-radius: 3px; padding: 6px 8px; background: #f2f5f3; color: #546159; font-size: 9px; font-style: normal; }
.detail-section ul { display: grid; gap: 8px; margin: 10px 0 0; padding: 0; list-style: none; }
.detail-section li { position: relative; padding-left: 14px; color: #657169; font-size: 10px; line-height: 1.55; }
.detail-section li::before { content: ''; width: 5px; height: 5px; position: absolute; left: 0; top: 5px; border-radius: 50%; background: #4a7bd7; }
.detail-actions { display: grid; grid-template-columns: 1fr 104px; gap: 8px; padding-top: 20px; }
.detail-actions button { height: 42px; display: flex; align-items: center; justify-content: center; gap: 7px; border-radius: 4px; font-size: 10px; font-weight: 600; }
.use-reference { border: 0; background: #2563eb; color: #fff; box-shadow: 0 7px 15px rgba(37,99,235,.2); }
.use-reference:hover { background: #1d4ed8; }
.secondary-action { border: 1px solid #dce2dd; background: #fff; color: #657169; }
.secondary-action:hover { border-color: #d7b35a; color: #a47312; }
.case-detail-enter-active, .case-detail-leave-active { transition: opacity .2s ease; }
.case-detail-enter-active .case-detail-panel, .case-detail-leave-active .case-detail-panel { transition: transform .24s ease; }
.case-detail-enter, .case-detail-leave-to { opacity: 0; }
.case-detail-enter .case-detail-panel, .case-detail-leave-to .case-detail-panel { transform: translateX(36px); }

@media (max-width: 1050px) {
  .case-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .detail-content { grid-template-columns: minmax(0, 1.35fr) 320px; }
}

@media (max-width: 767px) {
  .case-library { width: calc(100% - 16px); max-width: 100%; min-width: 0; margin-top: 18px; overflow-x: hidden; }
  .library-toolbar, .filter-panel, .case-grid, .case-card, .case-copy { width: 100%; max-width: 100%; min-width: 0; box-sizing: border-box; }
  .library-heading { align-items: flex-start; flex-direction: column; gap: 12px; }
  .library-heading h2 { font-size: 21px; }
  .library-summary { display: none; }
  .library-toolbar { align-items: stretch; flex-direction: column; }
  .case-search { width: 100%; }
  .sort-tabs { display: grid; grid-template-columns: repeat(3, 1fr); }
  .sort-tabs button { padding: 0 5px; white-space: normal; }
  .filter-panel { padding-right: 16px; }
  .filter-row { grid-template-columns: 38px minmax(0, 1fr); align-items: flex-start; }
  .filter-row > span { padding-top: 8px; }
  .reset-filter { position: static; width: max-content; margin-left: 50px; }
  .case-grid { grid-template-columns: 1fr; }
  .case-copy h3 { max-width: 100%; overflow-wrap: anywhere; font-size: 14px; line-height: 1.45; }
  .case-copy p { height: auto; min-height: 34px; }
  .case-meta { min-width: 0; flex-wrap: wrap; }
  .favorite-button { top: 10px; opacity: 1; }
  .cover-action { opacity: 1; transform: none; }
  .case-detail-panel { width: calc(100% - 58px); }
  .detail-header { min-height: 72px; padding: 12px 14px; }
  .detail-header h2 { max-width: 210px; overflow: hidden; font-size: 15px; text-overflow: ellipsis; white-space: nowrap; }
  .detail-content { display: block; overflow-y: auto; }
  .detail-gallery { height: 390px; grid-template-rows: minmax(0, 1fr) 70px; padding: 12px; }
  .detail-info { overflow: visible; padding: 16px; border-left: 0; }
  .detail-actions { grid-template-columns: 1fr; }
}
</style>
